import React, { Fragment, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router-dom'
import DefaultLayout from '../../components/DefaultLayout'
import Hero from '../../components/Hero/Hero'
import BalanceText from '../../components/BalanceText'
import { setSessionItem } from '../../storeManager'
import { languages } from '../../locale'
import PremiumButton from '../../components/PremiumButton/PremiumButton'
import Footer from '../../components/Footer/Footer'
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl'
import messages from './messages'
import { selectIsPremium, selectIsLoggedIn } from '../../store/userSlice'
import Review from '../../components/Review'
import heart from 'media/heart-bubble.svg'
import WhatIsLifesum from '../../components/WhatIsLifesum/WhatIsLifesum'
import Button from '../../components/Button/Button'
import QuizApple from 'media/apple-quiz.svg'
import useBreakpoints from '../../hooks/useBreakpoints'
import Awards from '../../components/Awards'
import Animation from 'js/components/Animation/Animation'
import loadable from '@loadable/component'
import { useInView } from 'react-intersection-observer'

const PlanTestOverlay = loadable(() =>
  import('js/components/PlanTest/PlanTestOverlay')
)

const Testimonials = loadable(() =>
  import('../../components/Testimonials/Testimonials')
)

function Home() {
  const [showPlanTest, setShowPlanTest] = useState(false)
  const isLoggedIn = useSelector(state => selectIsLoggedIn(state))
  const isPremium = useSelector(state => selectIsPremium(state))
  const history = useHistory()
  const { LG, XXL } = useBreakpoints()
  const [testamonialsRef, testamonialsInView] = useInView({
    triggerOnce: true,
    threshold: 0.8,
  })

  useEffect(() => {
    setSessionItem('bannerShown', true)
  }, [])

  const goToSignUpOrUpgrade = () => {
    if (isLoggedIn) {
      history.push('/upgrade')
    } else {
      history.push('/signup')
    }
  }

  return (
    <div className="bg-Bg">
      <DefaultLayout>
        <Helmet
          title="Lifesum Health App – Get Healthy & Lose Weight"
          meta={[
            {
              name: 'keywords',
              content:
                'bmi, diet journal, weight loss, bmi calculator, healthy recipes, how to lose weight, health, nutrition facts, weight loss online, free calorie counter, lose weight, diet, motivation, fitness, workout, exercise, cardio, paleo, gluten free, vegan, vegetarian, iphone, android, iOS, samsung, barcode, calories, step counter, app, gain weight, diabetes, muscle, ketogenic',
            },
            {
              name: 'description',
              content:
                'Personalized diet plans, nutrition advice & healthy recipes. Lose weight the healthy way with nutritious food in the right portion sizes.',
            },
          ]}
        >
          <link rel="canonical" href="https://lifesum.com/" />
          <link
            rel="alternate"
            href="https://lifesum.com/"
            hrefLang="x-default"
          />
          {languages.map(locale => {
            return (
              <link
                rel="alternate"
                href={
                  locale === 'en'
                    ? `https://lifesum.com/`
                    : `https://lifesum.com/${locale}/`
                }
                hrefLang={locale}
                key={locale}
              />
            )
          })}
        </Helmet>
        <Hero
          premium={isPremium}
          onClickFunc={() => {
            goToSignUpOrUpgrade()
          }}
        />
        <div className="c-startpage text-Type">
          <div className="o-width-limiter">
            <div className="-mt-56 mb-64 lg:hidden">
              <Awards />
            </div>
            <h2 className="font-bold xl:font-regular text-40 lg:text-55 xl:text-72 leading-tighter text-center xl:text-left lg:pt-56 xl:pt-64 mb-64 xl:mb-80">
              <FormattedMessage {...messages.what_is_lifesum} />
            </h2>
            <WhatIsLifesum />
            <div className="bg-BgSub rounded-12 md:w-2/3 lg:w-21/24 xl:w-2/3 mx-auto lg:mt-24 xl:mt-64 mb-96 px-16 lg:px-64 pt-24 lg:pt-40 pb-40 xl:mb-128 text-center lg:text-left">
              <div className="lg:flex">
                <img
                  src={QuizApple}
                  alt=""
                  className="mb-16 lg:mb-0 relative lg:mr-64 flex-shrink-0"
                  style={{ width: XXL ? '240px' : LG ? '200px' : '170px' }}
                />
                <div>
                  <h2 className="mb-36 text-32 lg:text-47 xxl:text-55 font-bold leading-tighter max-w-768 mx-auto">
                    <FormattedMessage {...messages.diet_quiz_heading} />
                  </h2>
                  <Button
                    big
                    style={{ minWidth: LG ? '240px' : '0' }}
                    onClick={() => {
                      setShowPlanTest(true)
                    }}
                  >
                    <FormattedMessage
                      {...messages['CTA.diet_test_header_button']}
                    />
                  </Button>
                  {showPlanTest && (
                    <PlanTestOverlay onClose={() => setShowPlanTest(false)} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-BgSub">
          <img
            className="mx-auto block mb-12 relative"
            src={heart}
            alt=""
            style={{
              bottom: 40,
              width: 130,
            }}
          />
          <div className="o-width-limiter">
            <div className="mx-auto max-w-1280 pb-56">
              <div className="hidden lg:block mb-56">
                <Awards />
              </div>
              <div className="md:flex">
                <div className="md:w-1/3 px-16">
                  <Review
                    text={
                      <FormattedMessage {...messages.appStoreReviewsText2} />
                    }
                  />
                </div>
                <div className="md:w-1/3 px-16">
                  <Review
                    text={
                      <FormattedMessage {...messages.appStoreReviewsText1} />
                    }
                  />
                </div>
                <div className="md:w-1/3 px-16">
                  <Review
                    text={
                      <FormattedMessage {...messages.appStoreReviewsText3} />
                    }
                  />
                </div>
              </div>
              <div className="max-w-768 mx-auto" ref={testamonialsRef}>
                {testamonialsInView && <Testimonials />}
              </div>
            </div>
          </div>
          {!isPremium && (
            <Fragment>
              <h2 className="text-40 lg:text-47 xxl:text-55 text-center font-bold leading-tighter px-16 mx-auto max-w-968  md:w-4/5">
                <BalanceText>
                  <FormattedHTMLMessage
                    {...messages.homepage_motivate_to_sign_up}
                  />
                </BalanceText>
              </h2>
              <PremiumButton
                onClickFunc={() => {
                  goToSignUpOrUpgrade()
                }}
                newStartpage={true}
                GALabel="Bottom desktop CTA"
              />
            </Fragment>
          )}
          <Animation
            name="apple-lift"
            className="c-startpage__animation z-10 relative w-17/24 mt-40 md:w-half mx-auto"
            style={{ maxWidth: '400px' }}
          ></Animation>
        </div>
        <Footer marginless />
      </DefaultLayout>
    </div>
  )
}

export default Home
